import React, { FC, MutableRefObject, ReactElement, useContext, useEffect, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { InsightsTopBar } from '../../Molecules/Insights/InsightsTopBar'
import { FormProvider, useForm, UseFormMethods } from '../../../../Vendor/react-hook-form/dist/index.ie11'
import { ReactState } from '../../../../React/Common/Types/Types'
import { InsightsDTO, InsightsStorage } from '../../../../React/InsightsHub/Types/Types'
import { usePersistentState } from '../../../../React/Common/Hooks/usePersistState'
import { INSIGHTS_STORAGE_KEY } from '../../../../React/InsightsHub/Constants/Constants'
import { isStringNotEmpty, IsVarEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { InsightsDashboardLanguage } from '../../../../../i18n'
import { PARTITION_FILTER_NAME, TELSYS_FILTER_NAME } from '../../../../React/InsightsHub/Constants/FieldNames'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext } from '../../../../React/Common/Contexts'
import { InsightsStats } from '../../Molecules/Insights/InsightsStats'
import { InsightsInformation } from '../../Molecules/Insights/InsightsInformation'
import { InsightsLoader } from '../../Molecules/Insights/InsightsLoader'
import { newInsightsTour } from '../../../../React/UserTour/Flows/NewInsightsTour'
import { ArrayUtils } from '../../../../NonReact/utils/ArrayUtils'
import { SelectOptions } from '../../../../React/Common/Components/Selects'
import { requestJSONAsync } from '../../../../React/Common/API/AsyncFetching'
import { insightsHubAPI, insightsHubStatus } from '../../../../React/InsightsHub/Constants/APIConstants'
import { RESTMethods } from '../../../../NonReact/constants/REST'
import { useFeatureFlag } from '../../../../React/Common/Hooks/useFeatureFlag'
import { Branding } from '../../../../React/Common/API/BrandingAPI'
import { useBranding } from '../../../../React/Common/Hooks/useBranding'
import { redirectToServletWithBranding, ServletCmds } from '../../../../NonReact/constants/ServletCommands'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'
import { TimingOptions } from '../../../../NonReact/common/TimingOptions'
import { JSONResponse } from '../../../../NonReact/common/Types/Types'

export const InsightsDashboard: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(InsightsDashboardLanguage.NAME)

    const newInsightsEnabled: Definable<boolean> = useFeatureFlag('enableNewInsights')

    const branding: Nullable<Branding> = useBranding()

    const methods: UseFormMethods = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        shouldUnregister: false
    })

    const { watch }: UseFormMethods = methods

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [isDataLoading, setIsDataLoading]: ReactState<boolean> = useState<boolean>(true)
    const isLoadingTelServersState: ReactState<boolean> = useState<boolean>(true)
    const [isLoadingTelServers]: ReactState<boolean> = isLoadingTelServersState
    const isLoadingPartitionsState: ReactState<boolean> = useState<boolean>(true)
    const [isLoadingPartitions]: ReactState<boolean> = isLoadingPartitionsState
    const availableTelServersState: ReactState<Definable<SelectOptions<number>>> =
        useState<Definable<SelectOptions<number>>>()
    const [availableTelServers]: ReactState<Definable<SelectOptions<number>>> = availableTelServersState
    const availablePartitionsState: ReactState<Definable<SelectOptions<number>>> =
        useState<Definable<SelectOptions<number>>>()
    const [availablePartitions]: ReactState<Definable<SelectOptions<number>>> = availablePartitionsState

    const [insightsData, setInsightsData]: ReactState<Nullable<InsightsDTO>> = useState<Nullable<InsightsDTO>>()
    const [errorMessage, setErrorMessage]: ReactState<Nullable<string>> = useState<Nullable<string>>()

    const [insightsStorage, setInsightsStorage]: ReactState<Nullable<InsightsStorage>> = usePersistentState<
        Nullable<InsightsStorage>
    >(INSIGHTS_STORAGE_KEY, undefined)

    const telSysValue: number = watch(TELSYS_FILTER_NAME)
    const partitionValue: number = watch(PARTITION_FILTER_NAME)

    useEffect((): void => {
        if (isLoadingTelServers || isLoadingPartitions) return

        if (isVarNotEmpty(telSysValue) && isVarNotEmpty(partitionValue)) {
            setIsDataLoading(true)
            setErrorMessage(undefined)
            setInsightsData(undefined)

            setInsightsStorage({ telSys: telSysValue, partition: partitionValue })

            requestJSONAsync<InsightsDTO>(insightsHubAPI(telSysValue, partitionValue), insightsHubStatus, {
                method: RESTMethods.GET,
                timingOptions: new TimingOptions({
                    frequency: 3000,
                    maxChecks: 300
                })
            })
                .then((data: JSONResponse<InsightsDTO>): void => {
                    if (isMounted.current) {
                        setInsightsData(data)
                    }
                })
                .catch((error: Error): void => {
                    if (isMounted.current) {
                        setErrorMessage(error.message)
                    }
                })
                .finally((): void => {
                    if (isMounted.current) {
                        setIsDataLoading(false)
                    }
                })
        } else if (
            IsVarEmpty(telSysValue) ||
            ArrayUtils.isEmpty(availableTelServers) ||
            (ArrayUtils.isEmpty(availablePartitions) && IsVarEmpty(partitionValue))
        ) {
            setErrorMessage(language.get(InsightsDashboardLanguage.InsightsDashboard_ErrorState_No_TelSys_Or_Partition))
        }
    }, [
        telSysValue,
        partitionValue,
        isLoadingTelServers,
        isLoadingPartitions,
        availableTelServers,
        availablePartitions
    ])

    useEffect((): void => {
        newInsightsTour()
    }, [])

    useEffect((): void => {
        if (isVarNotEmpty(newInsightsEnabled) && !newInsightsEnabled && isVarNotEmpty(branding)) {
            redirectToServletWithBranding(ServletCmds.INSIGHTS_HUB, branding.URLMapping ?? EMPTY_STRING)
        }
    }, [newInsightsEnabled, branding])

    return (
        <FormProvider {...methods}>
            <div className='insights-dashboard'>
                <InsightsTopBar
                    isDataLoading={isDataLoading}
                    isLoadingPartitions={isLoadingPartitionsState}
                    isLoadingTelServers={isLoadingTelServersState}
                    insightsStorage={insightsStorage}
                    setErrorMessage={setErrorMessage}
                    availableTelServers={availableTelServersState}
                    availablePartitions={availablePartitionsState}
                />
                {isStringNotEmpty(errorMessage) ? (
                    <div className='id-error-message'>{errorMessage}</div>
                ) : (
                    <>
                        {isDataLoading || isLoadingTelServers || isLoadingPartitions ? (
                            <InsightsLoader />
                        ) : (
                            <div className='id-insights'>
                                <InsightsStats summaryInfo={insightsData?.SummaryInfo} />
                                <InsightsInformation insightsData={insightsData} />
                            </div>
                        )}
                    </>
                )}
            </div>
        </FormProvider>
    )
}
